import { useSetAtom } from "jotai";
import { Download, Github, Menu, Search } from "lucide-react";
import { Link } from "react-router-dom";

import { Logo } from "@components/Logo.tsx";
import { SearchPalette } from "@components/SearchPalette.tsx";
import { ThemeToggle } from "@components/ThemeToggle.tsx";
import { VersionSwitcher } from "@components/VersionSwitcher.tsx";
import { mobileNavOpenAtom, searchOpenAtom } from "@lib/state.ts";

const REPO_URL = import.meta.env.VITE_REPO_URL as string | undefined;
const DOC_ENV = import.meta.env.VITE_DOC_ENV as string | undefined;

interface Props {
  version: string;
}

export function Header({ version }: Props) {
  const setSearchOpen = useSetAtom(searchOpenAtom);
  const setMobileNavOpen = useSetAtom(mobileNavOpenAtom);

  return (
    <header
      className="sticky top-0 z-40 border-b border-line bg-bg/85 backdrop-blur"
      style={{ height: "var(--gt-header-h)" }}
    >
      <div className="flex h-full items-center gap-3 px-3 sm:px-4">
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          aria-label="Open navigation"
          className="rounded-sm2 p-1.5 text-muted transition hover:bg-surface-2 hover:text-ink lg:hidden"
        >
          <Menu className="size-4" aria-hidden />
        </button>

        <Link
          to={`/${version}`}
          aria-label="Global Tote API reference, home"
          className="flex shrink-0 items-center gap-2.5 rounded-sm2"
        >
          <Logo className="size-7" />
          <span className="hidden text-[15px] font-semibold tracking-tight text-ink sm:block">
            Global Tote
            <span className="ml-1.5 font-normal text-muted">API</span>
          </span>
        </Link>

        {DOC_ENV && DOC_ENV !== "prod" ? (
          <span className="rounded-sm2 bg-warning-tint px-1.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-warning">
            {DOC_ENV} build
          </span>
        ) : null}

        {/* Search trigger doubles as the keyboard-shortcut hint. The visible
            label collapses on small screens, so the control is labelled
            explicitly rather than relying on it. */}
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          aria-label="Search the documentation"
          aria-keyshortcuts="Meta+K Control+K"
          className="ml-auto flex items-center gap-2 rounded-default border border-line px-2.5 py-1.5 text-[13px] text-subtle transition hover:border-line-strong hover:text-muted sm:w-56"
        >
          <Search className="size-3.5 shrink-0" aria-hidden />
          <span className="hidden sm:block">Search</span>
          <kbd className="ml-auto hidden rounded-sm2 border border-line px-1.5 text-[10.5px] sm:block">
            ⌘K
          </kbd>
        </button>

        <div className="flex items-center gap-1">
          <VersionSwitcher version={version} />

          <a
            href={`/openapi/${version}.json`}
            download={`globaltote-openapi-${version}.json`}
            title="Download the OpenAPI document"
            aria-label="Download the OpenAPI document"
            className="rounded-sm2 p-1.5 text-muted transition hover:bg-surface-2 hover:text-ink"
          >
            <Download className="size-4" aria-hidden />
          </a>

          <ThemeToggle />

          {REPO_URL ? (
            <a
              href={REPO_URL}
              target="_blank"
              rel="noreferrer noopener"
              title="Source repository"
              aria-label="Source repository"
              className="hidden rounded-sm2 p-1.5 text-muted transition hover:bg-surface-2 hover:text-ink sm:block"
            >
              <Github className="size-4" aria-hidden />
            </a>
          ) : null}
        </div>
      </div>

      <SearchPalette version={version} />
    </header>
  );
}
